import { Link } from "react-router-dom";
import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import TikTokIcon from "./icons/TikTokIcon";
import { useTranslation } from "../lib/i18n";
import { MAPS_URL } from "../lib/office";

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer data-testid="site-footer" className="border-t border-black/10 bg-[#F5F5F5] mt-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-12">
          <div className="col-span-2 md:col-span-5">
            <img src="/brand/ege-logo-black.png" alt="Ege Models" data-testid="footer-logo" className="h-7 w-auto"/>
            <p className="font-serif text-xl md:text-2xl tracking-tighter leading-[1.15] mt-6 text-zinc-500">
              <span className="italic">{t("footer.tagline")}</span><br/>
              <span className="italic">{t("footer.tagline2")}</span>
            </p>
            <p className="mt-6 max-w-md text-zinc-600 leading-relaxed">{t("footer.about")}</p>
          </div>

          <div className="md:col-span-2">
            <p className="label-overline text-zinc-500 mb-5">{t("footer.navigate")}</p>
            <ul className="space-y-3">
              <li><Link to="/models" className="link-underline">Models</Link></li>
              <li><Link to="/archive" className="link-underline">The archive</Link></li>
              <li><Link to="/about" className="link-underline">{t("nav.about")}</Link></li>
              <li><Link to="/services" className="link-underline">{t("nav.services")}</Link></li>
              <li><Link to="/code-of-conduct" className="link-underline">Code of Conduct</Link></li>
              <li><Link to="/contact" className="link-underline">{t("nav.contact")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="label-overline text-zinc-500 mb-5">{t("footer.apply")}</p>
            <ul className="space-y-3">
              <li><Link to="/casting" className="link-underline">Now casting</Link></li>
              <li><Link to="/apply" className="link-underline">{t("nav.apply")}</Link></li>
              <li><Link to="/apply/guide" className="link-underline">Photo guide</Link></li>
              <li><Link to="/status" className="link-underline">Check status</Link></li>
              <li><Link to="/faq" className="link-underline">{t("nav.faq")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="label-overline text-zinc-500 mb-5">{t("footer.reachUs")}</p>
            <ul className="space-y-3 text-zinc-700">
              <li className="flex items-center gap-3">
                <Mail size={14}/>
                <a href="mailto:info@egemodels.com" data-testid="footer-email" className="link-underline">
                  info@egemodels.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14}/>
                <a href="tel:+31627505783" data-testid="footer-phone" className="link-underline">
                  +31 6 27505783
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={14}/>
                <a
                  href="https://wa.me/905350441515"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-whatsapp"
                  className="link-underline"
                >
                  +90 535 044 15 15  <span className="text-xs text-zinc-500 ml-1">· WhatsApp</span>
                </a>
              </li>
              <li className="flex items-start gap-3" data-testid="footer-address"><MapPin size={14} className="mt-0.5 shrink-0"/> <a href={MAPS_URL} target="_blank" rel="noreferrer" data-testid="footer-address-link" className="link-underline">Puck van Heelstraat 142<br/>3077 MG Rotterdam, Netherlands</a></li>
              <li>
                <a
                  href="https://instagram.com/egemodels"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram"
                  className="link-underline inline-flex items-center gap-3"
                >
                  <Instagram size={14}/> @egemodels
                </a>
              </li>
              <li>
                <a
                  href="https://www.tiktok.com/@egemodels"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-tiktok"
                  className="link-underline inline-flex items-center gap-3"
                >
                  <TikTokIcon size={14}/> @egemodels
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Offices */}
        <div className="mt-16 pt-8 border-t border-black/10 flex flex-wrap gap-x-8 gap-y-2" data-testid="footer-territories">
          {["Rotterdam"].map((c) => (
            <span key={c} className="label-overline text-zinc-500">{c}</span>
          ))}
        </div>

        <div className="mt-8 pt-8 border-t border-black/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="label-overline text-zinc-500">© {new Date().getFullYear()} Ege Models.</p>
          <div className="flex gap-6">
            <Link to="/partner/login" data-testid="footer-partner-portal" className="label-overline text-zinc-500 hover:text-black">Agency portal</Link>
            <Link to="/privacy" className="label-overline text-zinc-500 hover:text-black">{t("footer.privacy")}</Link>
            <Link to="/terms" className="label-overline text-zinc-500 hover:text-black">{t("footer.terms")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
